import React, { useState, useEffect } from 'react';
import ActivityForm from '../components/ActivityForm';
import ActivityList from '../components/ActivityList';
import { activitiesApi, leadsApi } from '../services/api';
import api from '../services/api';
import { Activity } from '../types/Activity';
import { Lead } from '../types/Lead';
import { User } from '../types/User';
import { useAuth } from '../contexts/AuthContext';

// Deal interface
interface Deal {
  id: number;
  name: string;
  lead: number | { id: number; name: string };
  status: string;
}
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Icons
const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const AlertIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="12"></line>
    <line x1="12" y1="16" x2="12.01" y2="16"></line>
  </svg>
);

const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3"></polygon>
  </svg>
);

const RefreshIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

const ListIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"></line>
    <line x1="8" y1="12" x2="21" y2="12"></line>
    <line x1="8" y1="18" x2="21" y2="18"></line>
    <line x1="3" y1="6" x2="3.01" y2="6"></line>
    <line x1="3" y1="12" x2="3.01" y2="12"></line>
    <line x1="3" y1="18" x2="3.01" y2="18"></line>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// Calendar View Component
interface CalendarViewProps {
  activities: Activity[];
  onEditActivity: (activity: Activity) => void;
  onCompleteActivity: (id: number) => void;
  onCreateActivity: (date: Date) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({ activities, onEditActivity, onCompleteActivity, onCreateActivity }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const startDate = startOfWeek(monthStart, { locale: ptBR });
  const endDate = endOfWeek(monthEnd, { locale: ptBR });

  const days = eachDayOfInterval({ start: startDate, end: endDate });

  const getActivitiesForDay = (day: Date): Activity[] => {
    return activities.filter(activity => {
      if (!activity.scheduled_at) return false;
      const activityDate = parseISO(activity.scheduled_at);
      return isSameDay(activityDate, day);
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'medium': return '#3b82f6';
      case 'low': return '#9ca3af';
      default: return '#6366f1';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#22c55e';
      case 'in_progress': return '#3b82f6';
      case 'cancelled': return '#9ca3af';
      default: return '#f59e0b';
    }
  };

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const selectedDayActivities = selectedDate ? getActivitiesForDay(selectedDate) : [];

  return (
    <div style={{ display: 'flex', gap: 'var(--space-4)', height: '100%' }}>
      {/* Calendar Grid */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Calendar Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-4)',
          padding: 'var(--space-2) 0'
        }}>
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            style={{
              padding: 'var(--space-2)',
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text-secondary)'
            }}
          >
            <ChevronLeftIcon />
          </button>
          <h2 style={{
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--text-primary)',
            textTransform: 'capitalize'
          }}>
            {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
          </h2>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            style={{
              padding: 'var(--space-2)',
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text-secondary)'
            }}
          >
            <ChevronRightIcon />
          </button>
        </div>

        {/* Week Days Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '1px',
          marginBottom: 'var(--space-2)'
        }}>
          {weekDays.map((day) => (
            <div
              key={day}
              style={{
                padding: 'var(--space-2)',
                textAlign: 'center',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--text-secondary)'
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '2px',
          background: 'var(--glass-border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden'
        }}>
          {days.map((day, index) => {
            const dayActivities = getActivitiesForDay(day);
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isToday = isSameDay(day, new Date());
            const isSelected = selectedDate && isSameDay(day, selectedDate);

            return (
              <div
                key={index}
                onClick={() => setSelectedDate(day)}
                style={{
                  minHeight: '100px',
                  padding: 'var(--space-2)',
                  background: isSelected
                    ? 'rgba(99, 102, 241, 0.1)'
                    : isCurrentMonth
                      ? 'var(--glass-bg)'
                      : 'rgba(0,0,0,0.02)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-fast)',
                  borderLeft: isSelected ? '3px solid var(--color-primary-500)' : '3px solid transparent'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 'var(--space-1)'
                }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    fontSize: 'var(--text-sm)',
                    fontWeight: isToday ? 'var(--font-weight-bold)' : 'var(--font-weight-medium)',
                    color: isToday
                      ? 'white'
                      : isCurrentMonth
                        ? 'var(--text-primary)'
                        : 'var(--text-tertiary)',
                    background: isToday ? 'var(--bg-gradient)' : 'transparent'
                  }}>
                    {format(day, 'd')}
                  </span>
                  {dayActivities.length > 0 && (
                    <span style={{
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-secondary)',
                      background: 'var(--glass-bg)',
                      padding: '2px 6px',
                      borderRadius: 'var(--radius-full)'
                    }}>
                      {dayActivities.length}
                    </span>
                  )}
                </div>

                {/* Activity Dots */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {dayActivities.slice(0, 3).map((activity, i) => (
                    <div
                      key={activity.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditActivity(activity);
                      }}
                      style={{
                        padding: '2px 6px',
                        fontSize: '11px',
                        borderRadius: 'var(--radius-sm)',
                        background: `${getPriorityColor(activity.priority)}15`,
                        borderLeft: `2px solid ${getPriorityColor(activity.priority)}`,
                        color: 'var(--text-primary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer',
                        textDecoration: activity.status === 'completed' ? 'line-through' : 'none',
                        opacity: activity.status === 'completed' ? 0.6 : 1
                      }}
                    >
                      {activity.title}
                    </div>
                  ))}
                  {dayActivities.length > 3 && (
                    <span style={{
                      fontSize: '10px',
                      color: 'var(--text-tertiary)',
                      paddingLeft: '6px'
                    }}>
                      +{dayActivities.length - 3} mais
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Day Panel */}
      {selectedDate && (
        <div style={{
          width: '320px',
          background: 'var(--glass-bg)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--glass-border)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Panel Header */}
          <div style={{
            padding: 'var(--space-4)',
            borderBottom: '1px solid var(--glass-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h3 style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--text-primary)',
                marginBottom: '2px'
              }}>
                {format(selectedDate, "d 'de' MMMM", { locale: ptBR })}
              </h3>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                {format(selectedDate, 'EEEE', { locale: ptBR })}
              </p>
            </div>
            <button
              onClick={() => setSelectedDate(null)}
              style={{
                padding: 'var(--space-1)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-tertiary)'
              }}
            >
              <XIcon />
            </button>
          </div>

          {/* Activities List */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: 'var(--space-3)'
          }}>
            {selectedDayActivities.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: 'var(--space-6)',
                color: 'var(--text-tertiary)'
              }}>
                <CalendarIcon />
                <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
                  Nenhuma tarefa para este dia
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {selectedDayActivities.map((activity) => (
                  <div
                    key={activity.id}
                    style={{
                      padding: 'var(--space-3)',
                      background: 'white',
                      borderRadius: 'var(--radius-lg)',
                      border: '1px solid var(--glass-border)',
                      cursor: 'pointer',
                      transition: 'all var(--transition-fast)'
                    }}
                    onClick={() => onEditActivity(activity)}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 'var(--space-2)'
                    }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activity.status !== 'completed') {
                            onCompleteActivity(activity.id);
                          }
                        }}
                        style={{
                          padding: '2px',
                          background: 'transparent',
                          border: 'none',
                          cursor: activity.status === 'completed' ? 'default' : 'pointer',
                          color: activity.status === 'completed' ? '#22c55e' : '#d1d5db',
                          flexShrink: 0,
                          marginTop: '2px'
                        }}
                      >
                        <CheckCircleIcon />
                      </button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--text-primary)',
                          textDecoration: activity.status === 'completed' ? 'line-through' : 'none',
                          marginBottom: '4px'
                        }}>
                          {activity.title}
                        </h4>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          flexWrap: 'wrap'
                        }}>
                          <span style={{
                            fontSize: '11px',
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-full)',
                            background: `${getStatusColor(activity.status)}15`,
                            color: getStatusColor(activity.status),
                            fontWeight: 'var(--font-weight-medium)'
                          }}>
                            {activity.status_display || activity.status}
                          </span>
                          <span style={{
                            fontSize: '11px',
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-full)',
                            background: `${getPriorityColor(activity.priority)}15`,
                            color: getPriorityColor(activity.priority),
                            fontWeight: 'var(--font-weight-medium)'
                          }}>
                            {activity.priority_display || activity.priority}
                          </span>
                        </div>
                        {activity.scheduled_at && (
                          <p style={{
                            fontSize: '11px',
                            color: 'var(--text-tertiary)',
                            marginTop: '4px'
                          }}>
                            {format(parseISO(activity.scheduled_at), "HH:mm", { locale: ptBR })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Task Button */}
          <div style={{
            padding: 'var(--space-3)',
            borderTop: '1px solid var(--glass-border)'
          }}>
            <button
              onClick={() => onCreateActivity(selectedDate)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-3)',
                background: 'var(--bg-gradient)',
                border: 'none',
                borderRadius: 'var(--radius-lg)',
                color: 'white',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-sm)'
              }}
            >
              <PlusIcon />
              Nova Tarefa
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Activities: React.FC = () => {
  const { user } = useAuth();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [deals, setDeals] = useState<any[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [defaultDate, setDefaultDate] = useState<Date | undefined>(undefined);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const data = await activitiesApi.list();
      const activities = Array.isArray(data) ? data : ((data as any).results || []);
      setActivities(activities);
      setFilteredActivities(activities);
    } catch (error) {
      console.error('Erro ao carregar tarefas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const data = await leadsApi.list();
      const leads = Array.isArray(data) ? data : ((data as any).results || []);
      setLeads(leads);
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/auth/users/company_users/');
      const data = response.data;
      const usersData = Array.isArray(data) ? data : ((data as any).results || []);
      // Mapeia para o formato esperado pelo componente
      const mappedUsers: User[] = usersData.map((u: any) => ({
        id: u.id,
        email: u.email,
        get_full_name: u.get_full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
        role: u.role
      }));
      setUsers(mappedUsers);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      // Fallback para lista vazia
      setUsers([]);
    }
  };

  const fetchDeals = async () => {
    try {
      const response = await api.get('/deals/deals/');
      const data = response.data;
      const deals = Array.isArray(data) ? data : ((data as any).results || []);
      setDeals(deals);
    } catch (error) {
      console.error('Erro ao carregar deals:', error);
      setDeals([]);
    }
  };

  useEffect(() => {
    fetchActivities();
    fetchLeads();
    fetchDeals();
    fetchUsers();
  }, []);

  const handleCreate = (date?: Date) => {
    setEditingActivity(undefined);
    setDefaultDate(date);
    setFormOpen(true);
  };

  const handleEdit = (activity: Activity) => {
    setEditingActivity(activity);
    setDefaultDate(undefined);
    setFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta tarefa?')) {
      return;
    }

    try {
      await activitiesApi.delete(id);
      setActivities(activities.filter(a => a.id !== id));
      setFilteredActivities(filteredActivities.filter(a => a.id !== id));
    } catch (error) {
      console.error('Erro ao excluir tarefa:', error);
      alert('Erro ao excluir tarefa');
    }
  };

  const handleComplete = async (id: number) => {
    try {
      const updated = await activitiesApi.complete(id);
      setActivities(activities.map(a => a.id === id ? updated : a));
      setFilteredActivities(filteredActivities.map(a => a.id === id ? updated : a));
    } catch (error) {
      console.error('Erro ao completar tarefa:', error);
      alert('Erro ao completar tarefa');
    }
  };

  const handleStart = async (id: number) => {
    try {
      const updated = await activitiesApi.start(id);
      setActivities(activities.map(a => a.id === id ? updated : a));
      setFilteredActivities(filteredActivities.map(a => a.id === id ? updated : a));
    } catch (error) {
      console.error('Erro ao iniciar tarefa:', error);
      alert('Erro ao iniciar tarefa');
    }
  };

  const handleCancel = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta tarefa?')) {
      return;
    }

    try {
      const updated = await activitiesApi.cancel(id);
      setActivities(activities.map(a => a.id === id ? updated : a));
      setFilteredActivities(filteredActivities.map(a => a.id === id ? updated : a));
    } catch (error) {
      console.error('Erro ao cancelar tarefa:', error);
      alert('Erro ao cancelar tarefa');
    }
  };

  const handleSave = async (data: any) => {
    try {
      setSubmitting(true);
      if (editingActivity) {
        const updated = await activitiesApi.update(editingActivity.id, data);
        setActivities(activities.map(a => a.id === editingActivity.id ? updated : a));
        setFilteredActivities(filteredActivities.map(a => a.id === editingActivity.id ? updated : a));
      } else {
        const created = await activitiesApi.create(data);
        setActivities([created, ...activities]);
        setFilteredActivities([created, ...filteredActivities]);
      }
      setFormOpen(false);
      setEditingActivity(undefined);
      setDefaultDate(undefined);
    } catch (error) {
      console.error('Erro ao salvar tarefa:', error);
      alert('Erro ao salvar tarefa');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter);

    if (filter === 'all') {
      setFilteredActivities(activities);
    } else if (filter === 'overdue') {
      const filtered = activities.filter(a => a.is_overdue && a.status !== 'completed');
      setFilteredActivities(filtered);
    } else {
      const filtered = activities.filter(a => a.status === filter);
      setFilteredActivities(filtered);
    }
  };

  const overdueCount = activities.filter(a => a.is_overdue && a.status !== 'completed').length;
  const todayCount = activities.filter(a => {
    const today = new Date().toDateString();
    const activityDate = new Date(a.scheduled_at || '').toDateString();
    return activityDate === today;
  }).length;

  const stats = [
    {
      label: 'Todas',
      value: activities.length,
      icon: <CalendarIcon />,
      color: 'var(--color-primary-500)',
      bgColor: 'rgba(99, 102, 241, 0.1)',
      filter: 'all'
    },
    {
      label: 'Agendadas',
      value: activities.filter(a => a.status === 'scheduled').length,
      icon: <ClockIcon />,
      color: 'var(--color-warning-500)',
      bgColor: 'rgba(245, 158, 11, 0.1)',
      filter: 'scheduled'
    },
    {
      label: 'Em Progresso',
      value: activities.filter(a => a.status === 'in_progress').length,
      icon: <PlayIcon />,
      color: 'var(--color-info-500)',
      bgColor: 'rgba(59, 130, 246, 0.1)',
      filter: 'in_progress'
    },
    {
      label: 'Concluídas',
      value: activities.filter(a => a.status === 'completed').length,
      icon: <CheckCircleIcon />,
      color: 'var(--color-success-500)',
      bgColor: 'rgba(34, 197, 94, 0.1)',
      filter: 'completed'
    },
    {
      label: 'Atrasadas',
      value: overdueCount,
      icon: <AlertIcon />,
      color: 'var(--color-error-500)',
      bgColor: 'rgba(239, 68, 68, 0.1)',
      filter: 'overdue'
    }
  ];

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: '1600px', margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-4)' }}>
          <div>
            <h1 style={{
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-2)'
            }}>
              Tarefas
            </h1>
            <p style={{ fontSize: 'var(--text-base)', color: 'var(--text-secondary)' }}>
              Gerencie todas as suas tarefas e compromissos
            </p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            {/* View Toggle */}
            <div style={{
              display: 'flex',
              background: 'var(--glass-bg)',
              backdropFilter: 'var(--glass-blur)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '4px'
            }}>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2) var(--space-3)',
                  background: viewMode === 'list' ? 'white' : 'transparent',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  color: viewMode === 'list' ? 'var(--color-primary-600)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-fast)',
                  fontWeight: viewMode === 'list' ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
                  fontSize: 'var(--text-sm)',
                  boxShadow: viewMode === 'list' ? 'var(--shadow-sm)' : 'none'
                }}
              >
                <ListIcon />
                Lista
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2) var(--space-3)',
                  background: viewMode === 'calendar' ? 'white' : 'transparent',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  color: viewMode === 'calendar' ? 'var(--color-primary-600)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-fast)',
                  fontWeight: viewMode === 'calendar' ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
                  fontSize: 'var(--text-sm)',
                  boxShadow: viewMode === 'calendar' ? 'var(--shadow-sm)' : 'none'
                }}
              >
                <CalendarIcon />
                Calendário
              </button>
            </div>

            <button
              onClick={() => fetchActivities()}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-3) var(--space-4)',
                background: 'var(--glass-bg)',
                backdropFilter: 'var(--glass-blur)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-lg)',
                color: 'var(--text-secondary)',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all var(--transition-fast)',
                opacity: loading ? 0.6 : 1
              }}
            >
              <RefreshIcon />
            </button>
            <button
              onClick={() => handleCreate()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-3) var(--space-5)',
                background: 'var(--bg-gradient)',
                border: 'none',
                borderRadius: 'var(--radius-lg)',
                color: 'white',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-md)',
                transition: 'all var(--transition-fast)'
              }}
            >
              <PlusIcon />
              Nova Tarefa
            </button>
          </div>
        </div>

        {/* Stats Cards - Only show in list view */}
        {viewMode === 'list' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 'var(--space-4)',
            marginBottom: 'var(--space-6)'
          }}>
            {stats.map((stat) => (
              <button
                key={stat.filter}
                onClick={() => handleFilterChange(stat.filter)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-4)',
                  background: selectedFilter === stat.filter ? 'var(--glass-bg-strong)' : 'var(--glass-bg)',
                  backdropFilter: 'var(--glass-blur)',
                  border: selectedFilter === stat.filter
                    ? `2px solid ${stat.color}`
                    : '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-xl)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-fast)',
                  textAlign: 'left'
                }}
              >
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: 'var(--radius-lg)',
                  background: stat.bgColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: stat.color
                }}>
                  {stat.icon}
                </div>
                <div>
                  <div style={{
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--text-primary)'
                  }}>
                    {stat.value}
                  </div>
                  <div style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-secondary)'
                  }}>
                    {stat.label}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Today indicator - Only show in list view */}
        {viewMode === 'list' && todayCount > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-3) var(--space-4)',
            background: 'rgba(99, 102, 241, 0.1)',
            borderRadius: 'var(--radius-lg)',
            marginBottom: 'var(--space-4)'
          }}>
            <CalendarIcon />
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-primary-600)' }}>
              Você tem <strong>{todayCount}</strong> tarefa{todayCount > 1 ? 's' : ''} para hoje
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minHeight: 0 }}>
        {viewMode === 'list' ? (
          <div style={{
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'var(--glass-blur)',
            borderRadius: 'var(--radius-2xl)',
            border: '1px solid var(--glass-border)',
            overflow: 'hidden'
          }}>
            <ActivityList
              activities={filteredActivities}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onComplete={handleComplete}
              onStart={handleStart}
              onCancel={handleCancel}
              loading={loading}
            />
          </div>
        ) : (
          <div style={{
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'var(--glass-blur)',
            borderRadius: 'var(--radius-2xl)',
            border: '1px solid var(--glass-border)',
            padding: 'var(--space-4)',
            height: '100%',
            minHeight: '600px'
          }}>
            {loading ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'var(--text-secondary)'
              }}>
                Carregando...
              </div>
            ) : (
              <CalendarView
                activities={activities}
                onEditActivity={handleEdit}
                onCompleteActivity={handleComplete}
                onCreateActivity={handleCreate}
              />
            )}
          </div>
        )}
      </div>

      {/* Activity Form Dialog */}
      <ActivityForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingActivity(undefined);
          setDefaultDate(undefined);
        }}
        onSave={handleSave}
        activity={editingActivity}
        leads={leads}
        deals={deals}
        users={users}
        isSubmitting={submitting}
        defaultDate={defaultDate}
        currentUserId={user?.id}
      />
    </div>
  );
};

export default Activities;
